import DateUtils from "src/services/utils/DateUtils";
import { INTERNATIONAL_DATE_FORMAT } from "src/common/constants/Dates";
import { KYBMonitoringTypes, KYBScreeningTypes, RisksRates } from "src/modules/3rd-party-management/constants";
import { formatDisplayValue } from "./index";

const COMPLIANCE_EXPORT_PAGE_SIZE = 500;
const TIMESTAMP_FORMAT = `${INTERNATIONAL_DATE_FORMAT} HH:mm`;

const SEARCH_INPUT_COLUMNS = [
    { key: "id", label: "ID", width: "6%" },
    { key: "status", label: "Status", width: "7%" },
    { key: "searchQuery", label: "Search Query (e.g., name)", width: "18%" },
    { key: "riskRating", label: "Risk Rating", width: "7%" },
    { key: "type", label: "Type", width: "8%" },
    { key: "dateOfBirth", label: "Date of Birth", width: "9%" },
    { key: "createdBy", label: "Created by", width: "12%" },
    { key: "createdOn", label: "Created on", width: "9%" },
    { key: "fuzziness", label: "Fuzziness", width: "7%" },
];

const SEARCH_RESULT_COLUMNS = [
    { key: "findings", label: "Findings", width: "6%" },
    { key: "falsePositive", label: "False Positive", width: "6%" },
    { key: "timestamp", label: "Timestamp", width: "11%" },
];

const resolveRiskRatingColor = (rating = "") => {
    const normalizedRating = `${rating}`.toLowerCase();

    return RisksRates[normalizedRating]?.color || RisksRates.low.color;
};

const formatComplianceDate = (dateUtils, value) => {
    if (!value) {
        return "-";
    }

    return dateUtils.convertDateToDate(value, INTERNATIONAL_DATE_FORMAT);
};

const formatComplianceTimestamp = (dateUtils, value) => {
    if (!value) {
        return "-";
    }

    if (typeof value === "number") {
        return dateUtils.convertTimeStampToDate(value, TIMESTAMP_FORMAT);
    }

    return dateUtils.convertDateToDate(value, TIMESTAMP_FORMAT);
};

const formatFuzziness = (value) => {
    if (value === null || value === undefined || value === "") {
        return "-";
    }

    const numericValue = Number(value);

    if (!Number.isNaN(numericValue)) {
        return `${numericValue}%`;
    }

    return formatDisplayValue(value);
};

const formatDatesOfBirth = (dateUtils, dates = []) => {
    if (!Array.isArray(dates) || !dates.length) {
        return "-";
    }

    return dates
        .map((date) => formatComplianceDate(dateUtils, date))
        .filter((date) => date !== "-")
        .join(", ") || "-";
};

const resolveFindingsLabel = (result = {}) => {
    if (Array.isArray(result?.findings) && result.findings.length) {
        return "View";
    }

    if (result?.evidencesCount) {
        return `${result.evidencesCount}`;
    }

    return "-";
};

const resolveFalsePositive = (result = {}, t) => {
    if (result?.is_false_positive === true || result?.isFalsePositive === true || result?.falsePositive === true) {
        return t("Yes");
    }

    if (result?.is_false_positive === false || result?.isFalsePositive === false || result?.falsePositive === false) {
        return t("No");
    }

    return "-";
};

const resolveResultTimestamp = (dateUtils, result = {}, fallbackValue) => {
    return formatComplianceTimestamp(
        dateUtils,
        result?.timestamp
            || result?.created_at
            || result?.createdAt
            || fallbackValue
    );
};

const buildMonitoringResultRow = (dateUtils, alert = {}, fallbackTimestamp, t) => ({
    findings: resolveFindingsLabel(alert),
    falsePositive: resolveFalsePositive(alert, t),
    timestamp: resolveResultTimestamp(dateUtils, alert, fallbackTimestamp),
    findingsDetails: (alert?.findings || [])
        .map((finding) => ({
            title: finding?.title || finding?.keyword || finding?.name,
            description: null,
            timestamp: finding?.created_at || finding?.release_date?.timestamp,
        }))
        .filter((finding) => finding.title),
});

const buildMonitoringExportRows = (monitoringData = {}, { t = (value) => value } = {}) => {
    const dateUtils = new DateUtils();
    const items = Array.isArray(monitoringData?.follow_list_results)
        ? monitoringData.follow_list_results
        : [];

    if (!items.length) {
        return [];
    }

    return items.flatMap((item) => {
        const followListItem = item?.kyb_compliance_monitoring_follow_list_item || {};
        const alerts = Array.isArray(item?.alerts) ? item.alerts : [];
        const primaryAlert = alerts[0] || {};
        const additionalAlerts = alerts.slice(1);

        const mainRow = {
            isSubRow: false,
            searchInput: {
                id: formatDisplayValue(item?.id),
                status: followListItem?.is_active ? t("Active") : t("Inactive"),
                searchQuery: formatDisplayValue(followListItem?.search_query),
                riskRating: item?.riskRating || followListItem?.rating || "low",
                type: t(KYBMonitoringTypes[followListItem?.type]?.title || followListItem?.type || "-"),
                dateOfBirth: formatComplianceDate(dateUtils, followListItem?.dateOfBirth),
                createdBy: formatDisplayValue(item?.createdBy?.fullName),
                createdOn: formatComplianceTimestamp(dateUtils, item?.created_at),
                fuzziness: formatFuzziness(
                    followListItem?.kyb_compliance_monitoring_follow_list?.fuzziness
                ),
            },
            searchResult: buildMonitoringResultRow(dateUtils, primaryAlert, item?.created_at, t),
        };

        const subRows = additionalAlerts.map((alert) => ({
            isSubRow: true,
            searchResult: buildMonitoringResultRow(dateUtils, alert, alert?.created_at, t),
        }));

        return [mainRow, ...subRows];
    });
};

const buildScreeningExportRows = (screeningData = {}, { t = (value) => value } = {}) => {
    const dateUtils = new DateUtils();
    const items = Array.isArray(screeningData?.result) ? screeningData.result : [];

    if (!items.length) {
        return [];
    }

    return items.flatMap((item) => {
        const query = item?.searchedPersonsQuery || {};
        const results = Array.isArray(item?.searchedPersonsResults) ? item.searchedPersonsResults : [];
        const primaryResult = results[0] || {};
        const additionalResults = results.slice(1);

        const mainRow = {
            isSubRow: false,
            searchInput: {
                id: formatDisplayValue(item?.id),
                status: "-",
                searchQuery: formatDisplayValue(query?.name),
                riskRating: query?.rating || "low",
                type: t(KYBScreeningTypes[item?.screeningType]?.title || item?.screeningType || "-"),
                dateOfBirth: formatComplianceDate(dateUtils, query?.dateOfBirth),
                createdBy: formatDisplayValue(item?.createdBy?.fullName),
                createdOn: formatComplianceDate(dateUtils, item?.createdAt),
                fuzziness: formatFuzziness(query?.threshold),
            },
            searchResult: {
                findings: resolveFindingsLabel(primaryResult),
                falsePositive: resolveFalsePositive(primaryResult, t),
                timestamp: resolveResultTimestamp(dateUtils, primaryResult, item?.createdAt),
                findingsDetails: [],
            },
        };

        const subRows = additionalResults.map((result) => ({
            isSubRow: true,
            searchResult: {
                findings: resolveFindingsLabel(result),
                falsePositive: resolveFalsePositive(result, t),
                timestamp: resolveResultTimestamp(dateUtils, result, item?.createdAt),
                findingsDetails: [],
            },
        }));

        return [mainRow, ...subRows];
    });
};

export {
    SEARCH_INPUT_COLUMNS,
    SEARCH_RESULT_COLUMNS,
    buildMonitoringExportRows,
    buildScreeningExportRows,
    resolveRiskRatingColor,
};
